import signalMark from "@/assets/signal-mark.svg";

// Decorative API-Exchange hero illustration: the real Signal mark (the official
// vector artwork, so it lines up 1:1 with the header logo) in a soft indigo
// glow, wired by dashed connectors to three source tiles (shield, document,
// building) that feed a pair of faux document panels — "one submission fans out
// to many markets." Purely aesthetic (aria-hidden). The glow routes through the
// brk accent via `currentColor` and warms toward Electric Violet at its core,
// echoing the brand book's Signal Gradient; panels/tiles use fill-/stroke-
// utilities.
export function ExchangeHeroGraphic({
  className = "",
}: {
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 440 250"
      className={`text-brk-accent ${className}`}
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="hg-glow" cx="50%" cy="50%" r="50%">
          <stop
            offset="0%"
            className="text-brk-violet"
            stopColor="currentColor"
            stopOpacity="0.32"
          />
          <stop offset="55%" stopColor="currentColor" stopOpacity="0.1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
        <filter id="hg-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow
            dx="0"
            dy="3"
            stdDeviation="5"
            floodColor="#202024"
            floodOpacity="0.08"
          />
        </filter>
      </defs>

      {/* ── Document panels (right) ───────────────────────────────── */}
      {/* Back panel — faint, tinted. */}
      <rect
        x="250"
        y="56"
        width="184"
        height="120"
        rx="16"
        className="fill-brk-accent"
        opacity="0.05"
      />
      <rect
        x="268"
        y="76"
        width="66"
        height="7"
        rx="3.5"
        className="fill-brk-accent"
        opacity="0.16"
      />
      <rect
        x="268"
        y="92"
        width="120"
        height="5"
        rx="2.5"
        className="fill-brk-border"
      />
      <rect
        x="268"
        y="104"
        width="96"
        height="5"
        rx="2.5"
        className="fill-brk-border"
      />
      <rect
        x="268"
        y="116"
        width="108"
        height="5"
        rx="2.5"
        className="fill-brk-border"
      />
      {/* Floating card — white, shadowed. */}
      <g filter="url(#hg-shadow)">
        <rect
          x="322"
          y="70"
          width="104"
          height="80"
          rx="12"
          className="fill-brk-panel stroke-brk-border"
          strokeWidth="1.25"
        />
      </g>
      <rect
        x="336"
        y="84"
        width="44"
        height="6"
        rx="3"
        className="fill-brk-accent"
        opacity="0.5"
      />
      <rect
        x="336"
        y="99"
        width="70"
        height="4"
        rx="2"
        className="fill-brk-border"
      />
      <rect
        x="336"
        y="108"
        width="54"
        height="4"
        rx="2"
        className="fill-brk-border"
      />
      <rect
        x="336"
        y="117"
        width="62"
        height="4"
        rx="2"
        className="fill-brk-border"
      />
      {/* send arrow */}
      <path
        d="M394 134h14m0 0-4-4m4 4-4 4"
        className="stroke-brk-accent"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* ── Dashed connectors (behind the glow + tiles) ───────────── */}
      <g
        className="stroke-brk-border"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="1.5 7"
      >
        <path d="M180 118 C162 92 138 78 120 68" />
        <path d="M180 118 C140 122 100 126 72 128" />
        <path d="M180 118 C182 150 184 172 185 190" />
      </g>

      {/* ── Core: soft glow + the real Signal mark ────────────────── */}
      <circle cx="180" cy="118" r="56" fill="url(#hg-glow)" />
      <image href={signalMark} x="150" y="88" width="60" height="60" />

      {/* ── Source tiles (shield / document / building) ───────────── */}
      <g>
        {/* Shield (top) */}
        <g filter="url(#hg-shadow)">
          <rect
            x="100"
            y="26"
            width="40"
            height="40"
            rx="11"
            className="fill-brk-panel stroke-brk-border"
            strokeWidth="1.25"
          />
        </g>
        <path
          transform="translate(120 46)"
          d="M0 -10 L-8 -6.5 V-1 C-8 4.2 -4 7.2 0 9 C4 7.2 8 4.2 8 -1 V-6.5 Z M-3 -0.5 -0.6 2 3.4 -2.6"
          className="stroke-brk-text"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.72"
        />
        {/* Document (left) */}
        <g filter="url(#hg-shadow)">
          <rect
            x="26"
            y="108"
            width="40"
            height="40"
            rx="11"
            className="fill-brk-panel stroke-brk-border"
            strokeWidth="1.25"
          />
        </g>
        <path
          transform="translate(46 128)"
          d="M-6.5 -9 H2 L6.5 -4.5 V9 H-6.5 Z M2 -9 V-4.5 H6.5 M-3.5 1 H3.5 M-3.5 4.5 H3.5"
          className="stroke-brk-text"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.72"
        />
        {/* Building (bottom) */}
        <g filter="url(#hg-shadow)">
          <rect
            x="165"
            y="190"
            width="40"
            height="40"
            rx="11"
            className="fill-brk-panel stroke-brk-border"
            strokeWidth="1.25"
          />
        </g>
        <path
          transform="translate(185 210)"
          d="M-9 9 H9 M-6.5 9 V-2.5 L0 -8 L6.5 -2.5 V9 M-2.5 9 V4 H2.5 V9"
          className="stroke-brk-text"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.72"
        />
      </g>
    </svg>
  );
}
